import { effectiveExecutionProfile } from "./effectiveExecutionProfile";
import { isSetPerformed } from "./executionUi";
import { orderGroupItems } from "./groupRounds";
import { formatLoad, formatRir } from "./loadDisplay";
import { isUnmappedPlaceholder, snapshotSlotForItem } from "./substitution";

// Read-only presentation of a FINISHED execution for Review. Formatting only:
// every value is shown as it was entered, nothing is recomputed or converted,
// and "performed" is the same canonical rule the summary counts use.
// A set's `setType` is deliberately not presented: the Workout Log exposes no
// set-type concept (no warm-up prescription), so a stored WARMUP, TOP_SET...
// reads as the ordinary set row its values describe.
// A replacement is listed under its own name only: the exercise it replaced
// is history, shown in the saved workout's Details, not in Review.

export const REVIEW_ENTRY_KIND = Object.freeze({
  SETS: "sets",
  CARDIO: "cardio",
  PLACEHOLDER: "placeholder",
  UNLOGGABLE: "unloggable",
});

function pluralize(count, singular) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

// The value columns an item's rows share, in display order.
export function reviewSetColumns(profile, rirEnabled) {
  if (profile?.measurementType === "TIMED_HOLD") {
    return profile.loadMode === "NONE" ? ["amount"] : ["amount", "load"];
  }
  return [
    profile?.loadMode === "NONE" ? null : "load",
    "amount",
    rirEnabled ? "rir" : null,
  ].filter(Boolean);
}

export function reviewSetLine(profile, set, rirEnabled, ordinal) {
  const timed = profile?.measurementType === "TIMED_HOLD";
  const values = {
    load: profile?.loadMode === "NONE" ? "" : formatLoad(set.load),
    amount: timed
      ? (set.seconds == null ? "" : `${set.seconds} s`)
      : (set.reps == null ? "" : pluralize(set.reps, "rep")),
    rir: !timed && rirEnabled ? formatRir(set.rir) : "",
  };
  return {
    key: set.setId || `${set.setIndex}`,
    // The row's current 1-based place, as on the workout screen; `setIndex`
    // is the structural index and keeps gaps after a removed set.
    ordinal,
    performed: isSetPerformed(profile, set, rirEnabled),
    values,
    empty: !values.load && !values.amount && !values.rir,
  };
}

function reviewEntry(record, item, laneLabel = null) {
  const name = item.exerciseName || snapshotSlotForItem(record, item)?.exercise?.exerciseName || "Exercise";
  if (isUnmappedPlaceholder(record, item)) {
    const planned = (snapshotSlotForItem(record, item)?.exercise?.sets || []).length;
    return {
      kind: REVIEW_ENTRY_KIND.PLACEHOLDER,
      key: item.itemId,
      item,
      laneLabel,
      name: "No exercise chosen",
      status: planned ? `${pluralize(planned, "planned set")} not performed` : "Not performed",
      canNote: false,
    };
  }
  const profile = effectiveExecutionProfile(record, item);
  const shared = {
    key: item.itemId,
    item,
    laneLabel,
    name,
    skipped: Boolean(item.skipped),
    notes: item.notes ? String(item.notes) : "",
    canNote: true,
  };
  if (!profile) return { ...shared, kind: REVIEW_ENTRY_KIND.UNLOGGABLE };
  if (profile.measurementType === "CARDIO_MINUTES") {
    const completed = item.cardio?.completed === true && Number(item.cardio.minutes) >= 1;
    return {
      ...shared,
      kind: REVIEW_ENTRY_KIND.CARDIO,
      cardio: { completed, value: completed ? `${item.cardio.minutes} min` : "" },
    };
  }
  const rirEnabled = profile.measurementType === "REPS" &&
    record?.server?.policy?.rirInputEnabled !== false;
  return {
    ...shared,
    kind: REVIEW_ENTRY_KIND.SETS,
    columns: reviewSetColumns(profile, rirEnabled),
    // A value the athlete may leave out is simply absent, never a dash.
    optionalColumns: profile.loadMode === "OPTIONAL_WEIGHT" ? ["load"] : [],
    sets: (item.sets || []).map((set, index) => reviewSetLine(profile, set, rirEnabled, index + 1)),
  };
}

function groupLabel(block) {
  if (block?.blockType === "SUPERSET") return "Superset";
  if (block?.blockType === "GIANT_SET") return "Giant set";
  if (block?.blockType === "CIRCUIT") return "Circuit";
  return "Grouped set";
}

// The workout in the order the workout screen shows it: snapshot blocks (a
// grouped block keeps its lanes together), then the exercises added on top.
export function buildReviewSections(record) {
  const items = record?.document?.items || [];
  const blocks = record?.server?.snapshot?.blocks || [];
  const represented = new Set();
  const sections = [];
  blocks.forEach((block) => {
    const blockItems = items.filter((item) => item.blockKey === block.blockKey);
    if (!blockItems.length) return;
    blockItems.forEach((item) => represented.add(item.itemId));
    if (block.grouping === "GROUP") {
      sections.push({
        key: block.blockKey,
        group: groupLabel(block),
        entries: orderGroupItems(blockItems).map((item, index) => reviewEntry(record, item, `A${index + 1}`)),
      });
      return;
    }
    blockItems.forEach((item) => {
      sections.push({ key: item.itemId, group: null, entries: [reviewEntry(record, item)] });
    });
  });
  items.filter((item) => !represented.has(item.itemId)).forEach((item) => {
    sections.push({ key: item.itemId, group: null, entries: [reviewEntry(record, item)] });
  });
  return sections;
}
